import React, { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Overlay, Popover } from 'react-bootstrap';
import ClickableDiv from '../../atoms/clickableDiv';
import Text from '../../atoms/text/index';
import { AddressItem, AddressPayload } from '../../types/AddressPayload';
import { AlignEnum, ColorNameEnum, SizeEnum } from '../../types/enums/textStyle';
import ButtonGroupComponent from '../button-group/index';
import {
  ACCEPTING_NEW_PATIENTS_OLD_MESSAGE,
  AcceptingNewPatientsBadge,
  NOT_ACCEPTING_NEW_PATIENTS_OLD_MESSAGE,
} from './AcceptingNewPatients';
import './index.less';
import { Tooltip } from '../../atoms';

const getDisplayAddress = (selectedAddress): AddressItem => {
  const address = selectedAddress || { text: 'Location unavailable' };
  return address as AddressItem;
};

/**
 * Setting to true as the FFs are opened for all envs, and this will eliminate the need to migrate the app to the new FF package
 * There is dead code that may now be removed, leaving this to another effort
 */
const useNewPatientsIconsOnAddress = true;
const useAcceptNewPatientsBadgesOnAddress = true;

function AddressSelector({
  selectedAddress,
  value,
  items,
  onChange,
  shouldDisplayNewPatientsIcon,
}: AddressPayload & { shouldDisplayNewPatientsIcon: boolean }) {
  const [displayedAddress, setDisplayedAddress] = useState<AddressItem>(
    getDisplayAddress(selectedAddress),
  );
  const [show, setShow] = useState(false);
  const target = useRef(null);

  const dropdown = items.length > 1;

  const addressesItems = useMemo(
    () =>
      items.map((item) => {
        if (
          useAcceptNewPatientsBadgesOnAddress &&
          item.acceptNewPatients !== undefined &&
          item.acceptNewPatients !== null
        ) {
          return {
            ...item,
            secondaryDescription: useNewPatientsIconsOnAddress ? (
              <AcceptingNewPatientsBadge
                item={item}
                shouldDisplayNewPatientsIcon={shouldDisplayNewPatientsIcon}
              />
            ) : (
              <div>
                <span
                  className={classNames('address-accept-new-patients-bullet-container ', {
                    'address-accept-new-patients-blue-bullet': item.acceptNewPatients,
                    'address-accept-new-patients-gray-bullet': !item.acceptNewPatients,
                  })}
                >
                  ●
                </span>
                <span className="address-accept-new-patients-description">
                  {item.acceptNewPatients
                    ? ACCEPTING_NEW_PATIENTS_OLD_MESSAGE
                    : NOT_ACCEPTING_NEW_PATIENTS_OLD_MESSAGE}
                </span>
              </div>
            ),
          };
        }

        return item;
      }),
    [
      items,
      useAcceptNewPatientsBadgesOnAddress,
      useNewPatientsIconsOnAddress,
      shouldDisplayNewPatientsIcon,
    ],
  );

  return (
    <div ref={target}>
      <ClickableDiv
        onClick={() => {
          if (dropdown) {
            setShow(!show);
          }
        }}
        className={classNames(
          'address-selector-wrap',
          selectedAddress && selectedAddress.text !== 'Location unavailable'
            ? ''
            : 'location-unavailable',
          {
            dropdown,
          },
        )}
      >
        <Tooltip
          tooltipContent={displayedAddress?.text ?? ''}
          pillStyle
          contentMaxLength={47}
          conditionalTooltip
          placement="top"
          referenceClassName="tooltip-ref"
          bgColor="light"
        >
          <Text
            size={SizeEnum['16px']}
            align={AlignEnum.left}
            colorName={ColorNameEnum.themeSecondary}
            className="address-icon icon-map-pin-e"
            inline
          />
          <Text
            className="address-text"
            size={SizeEnum['12px']}
            align={AlignEnum.left}
            colorName={ColorNameEnum.ultraDark}
            text={displayedAddress?.text ?? ''}
            inline
          />
          {dropdown && (
            <Text
              size={SizeEnum['12px']}
              align={AlignEnum.left}
              colorName={ColorNameEnum.ultraDark}
              className="arrow-icon icon-chevron-down"
              inline
            />
          )}
        </Tooltip>
      </ClickableDiv>
      <Overlay
        show={show}
        target={target}
        rootClose
        onHide={() => setShow(false)}
        placement="bottom-start"
      >
        <Popover className="address-dropdown-popover btn-group-dropdown-popover" id="popover">
          <div className="popover-content">
            <ButtonGroupComponent
              bgColor="default"
              buttonType="dropdownOption"
              width="block"
              direction="column"
              color="extraDarkGray"
              onChange={(addressIndex) => {
                const address = items.find((item) => item.value === addressIndex);
                if (address) {
                  setDisplayedAddress(address);
                  if (onChange) onChange(address);
                }
                setShow(false);
              }}
              multiLines
              value={value}
              items={addressesItems}
            />
          </div>
        </Popover>
      </Overlay>
    </div>
  );
}

export default AddressSelector;
